import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '../stores/authStore'
import { useMaintenanceStore } from '../stores/maintenanceStore'
import { apiService } from '../services/apiService'
import { getParentOrg, setParentOrg } from '../utils/parentOrgCache'

const routes = [
  {
    path: '/',
    component: () => import("../layouts/default/DefaultLayout.vue"),
    children: [
      {
        path: "",
        name: 'AccommodationIndex',
        component: () => import("../views/AccommodationIndex.vue"),
        meta: { requiresAuth: true, title: 'Accommodations' },
      },
      {
        path: "accommodations/:id",
        name: 'AccommodationDetails',
        component: () => import("../views/AccommodationDetails.vue"),
        meta: { requiresAuth: true }, // Title set dynamically in component
      },
      // {
      //   path: "Accommodations-v2",
      //   name: 'AccommodationSecondIndex',
      //   component: () => import("../views/AccommodationSecondIndex.vue"),
      //   meta: { requiresAuth: true },
      // },
      {
        path: 'profile',
        name: 'Profile',
        component: () => import("../views/ProfileView.vue"),
        meta: { requiresAuth: true, title: 'My Profile' },
      },
      {
        path: 'help',
        name: 'Resources',
        component: () => import("../views/ResourcesView.vue"),
        meta: { requiresAuth: true, title: 'Resources' },
      }
    ]
  },
  {
    path: '/admin',
    name: 'AdminDashboard',
    component: () => import("../layouts/admin/AdminDashboardLayout.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
    children: [
      {
        path: '',
        name: 'AdminDashboardHome',
        component: () => import("../views/admin/AdminDashboardIndexView.vue"),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'Admin Overview' },
      },
      {
        path: 'organisations',
        name: 'AdminDashboardOrganisations',
        component: () => import("../views/dashboard/OrganisationsView.vue"),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'Organisations' },
      },
      {
        path: 'accommodations',
        name: 'AdminDashboardAccommodations',
        component: () => import("../views/dashboard/AccommodationsView.vue"),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'Accommodations' },
      },
      {
        path: 'users',
        name: 'AdminDashboardUsers',
        component: () => import("../views/dashboard/UsersView.vue"),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'Users' },
      },
      {
        path: 'user-invitations',
        name: 'AdminDashboardUserInvitations',
        component: () => import("../views/dashboard/UsersView.vue"),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'User Invitations' },
      },
      // REMOVED: /notifications route - functionality moved to notification dialog
      // {
      //   path: 'notifications',
      //   name: 'Notifications',
      //   component: () => import("../views/NotificationsPage.vue"),
      //   meta: { requiresAuth: true },
      // },
      {
        path: 'amenities',
        name: 'AdminDashboardAmenities',
        component: () => import("../views/dashboard/AmenityView.vue"),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'Amenities' },
      },
      {
        path: 'access-logs',
        name: 'AdminDashboardAccessLogs',
        component: () => import("../views/admin/AccessLogsView.vue"),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'Access Log' },
      },
      {
        path: 'analytics/users',
        name: 'AdminDashboardAnalyticsUsers',
        component: () => import("../views/admin/AnalyticsView.vue"),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'Analytics - Users' },
      },
      {
        path: 'analytics/accommodation-views',
        name: 'AdminDashboardAnalyticsAccommodationViews',
        component: () => import("../views/admin/AnalyticsView.vue"),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'Analytics - Accommodation Views' },
      },
      {
        path: 'analytics/booking/accommodation',
        name: 'AdminBookingAnalyticsAccommodation',
        component: () => import("../views/admin/BookingAnalyticsView.vue"),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'Booking Analytics - Accommodation' },
      },
      {
        path: 'analytics/booking/organisation',
        name: 'AdminBookingAnalyticsOrganisation',
        component: () => import("../views/admin/BookingAnalyticsView.vue"),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'Booking Analytics - Organisation' },
      },
      {
        path: 'analytics/booking/region',
        name: 'AdminBookingAnalyticsRegion',
        component: () => import("../views/admin/BookingAnalyticsView.vue"),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'Booking Analytics - Region' },
      },
      {
        path: 'analytics/booking/client-group',
        name: 'AdminBookingAnalyticsClientGroup',
        component: () => import("../views/admin/BookingAnalyticsView.vue"),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'Booking Analytics - Client Group' },
      },
      {
        path: 'analytics/booking',
        name: 'AdminBookingAnalytics',
        redirect: { name: 'AdminBookingAnalyticsAccommodation' },
      },
      {
        path: 'analytics/favourites/organisation',
        name: 'AdminFavouritesAnalyticsOrganisation',
        component: () => import("../views/admin/FavouritesAnalyticsView.vue"),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'Favourites Analytics - Organisation' },
      },
      {
        path: 'analytics/favourites/accommodation',
        name: 'AdminFavouritesAnalyticsAccommodation',
        component: () => import("../views/admin/FavouritesAnalyticsView.vue"),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'Favourites Analytics - Accommodation' },
      },
      {
        path: 'analytics/favourites',
        name: 'AdminFavouritesAnalytics',
        redirect: { name: 'AdminFavouritesAnalyticsOrganisation' },
      },
      {
        path: 'analytics/cohort-conflicts',
        name: 'AdminCohortConflictsAnalytics',
        component: () => import("../views/admin/CohortConflictsAnalyticsView.vue"),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'Analytics - Cohort Conflicts' },
      },
      {
        path: 'analytics/compliance',
        name: 'AdminDashboardAnalyticsCompliance',
        component: () => import("../views/admin/AnalyticsView.vue"),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'Analytics - Compliance' },
      },
      {
        path: 'analytics/dashboard',
        name: 'AdminDashboardAnalyticsDashboard',
        component: () => import("../views/admin/AnalyticsDashboardView.vue"),
        meta: { requiresAuth: true, requiresAdmin: true, title: 'Analytics' },
      },
      {
        path: 'analytics',
        name: 'AdminDashboardAnalytics',
        redirect: { name: 'AdminDashboardAnalyticsDashboard' },
      },
    ]
  },
  {
    path: '/accommodations/:id/edit/:step?',
    name: 'DashboardAccommodationEdit',
    component: () => import("../views/dashboard/AccommodationFormPage.vue"),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Edit Accommodation' },
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import("../layouts/dashboard/OrganisationDashboardLayout.vue"),
    meta: { requiresAuth: true, requiresManager: true },
    children: [
      {
        path: 'organisations/:id',
        name: 'DashboardOrganisationOverview',
        component: () => import("../views/dashboard/OrganisationOverviewView.vue"),
        meta: { requiresAuth: true, requiresManager: true, requiresOrgAccess: true, title: 'Organisation Overview' },
      },
      {
        path: 'organisations/:id/users',
        name: 'DashboardOrganisationUsers',
        component: () => import("../views/dashboard/UsersView.vue"),
        meta: { requiresAuth: true, requiresManager: true, requiresOrgAccess: true, title: 'Organisation Users' },
      },
      {
        path: 'organisations/:id/user-invitations',
        name: 'DashboardOrganisationUserInvitations',
        component: () => import("../views/dashboard/UsersView.vue"),
        meta: { requiresAuth: true, requiresManager: true, requiresOrgAccess: true, title: 'Organisation Invitations' },
      },
      {
        path: 'organisations/:id/teams',
        name: 'DashboardOrganisationTeams',
        component: () => import("../views/dashboard/OrganisationTeamsView.vue"),
        meta: { requiresAuth: true, requiresManager: true, requiresOrgAccess: true, title: 'Organisation Teams' },
      },
      // UNUSED: Account route - placeholder view with no navigation links pointing to it
      // {
      //   path: 'account',
      //   name: 'DashboardAccount',
      //   component: () => import("../views/dashboard/AccountView.vue"),
      //   meta: { requiresAuth: true, requiresManager: true, title: 'Account Settings' },
      // },
    ]
  },
  {
    path: '/dashboard/teams',
    name: 'TeamDashboard',
    component: () => import("../layouts/team/TeamDashboardLayout.vue"),
    meta: { requiresAuth: true, requiresManager: true },
    children: [
      {
        path: ':id',
        name: 'DashboardTeamOverview',
        component: () => import("../views/team/TeamOverviewView.vue"),
        meta: { requiresAuth: true, requiresManager: true, requiresOrgAccess: true, title: 'Team Overview' },
      },
      {
        path: ':id/accommodations',
        redirect: { name: 'DashboardTeamAccommodationsFavourited' }
      },
      {
        path: ':id/accommodations/favourited',
        name: 'DashboardTeamAccommodationsFavourited',
        component: () => import("../views/dashboard/TeamAccommodationsView.vue"),
        meta: { requiresAuth: true, requiresManager: true, requiresOrgAccess: true, title: 'Preferred Accommodations' },
      },
      {
        path: ':id/accommodations/all',
        name: 'DashboardTeamAccommodationsAll',
        component: () => import("../views/dashboard/TeamAccommodationsView.vue"),
        meta: { requiresAuth: true, requiresManager: true, requiresOrgAccess: true, title: 'All Accommodations' },
      },
      {
        path: ':id/users',
        name: 'DashboardTeamUsers',
        component: () => import("../views/dashboard/UsersView.vue"),
        meta: { requiresAuth: true, requiresManager: true, requiresOrgAccess: true, title: 'Team Users' },
      },
      {
        path: ':id/user-invitations',
        name: 'DashboardTeamUserInvitations',
        component: () => import("../views/dashboard/UsersView.vue"),
        meta: { requiresAuth: true, requiresManager: true, requiresOrgAccess: true, title: 'Team Invitations' },
      },
    ]
  },
  {
    path: '/terms',
    name: 'Terms',
    component: () => import("../views/TermsView.vue"),
    meta: { title: 'Terms of Use' },
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import("../views/LoginView.vue"),
    meta: { title: 'Login' },
  },
  {
    path: '/maintenance',
    name: 'Maintenance',
    component: () => import("../views/MaintenanceView.vue"),
    meta: { title: 'Maintenance' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import("../views/NotFoundView.vue"),
    meta: { title: 'Page Not Found' },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Custom scroll behavior for Vuetify layouts
  //
  // WHY THIS IS NEEDED:
  // Vuetify's <v-main scrollable> creates a custom scroll container instead of using the window.
  // When navigating between routes that share the same layout (e.g., AccommodationIndex → AccommodationDetails),
  // Vue reuses the layout component for performance, which preserves the scroll position in the v-main container.
  // This causes the new page to appear scrolled down instead of at the top.
  //
  // WHEN THIS IS REQUIRED:
  // - Navigation between child routes within the same parent layout (same v-main container is reused)
  // - Example: / → /accommodations/:id (both use DefaultLayout)
  //
  // WHEN THIS IS NOT NEEDED:
  // - Navigation between routes with different layouts (layout is destroyed and recreated)
  // - Example: /admin/accommodations → /accommodations/:id (AdminDashboardLayout → DefaultLayout)
  // - In this case, the new layout naturally starts at scroll position 0
  scrollBehavior(to, from, savedPosition) {
    return new Promise((resolve) => {
      // Wait for next tick to ensure DOM is updated
      setTimeout(() => {
        // Find the v-main__scroller element (actual scrollable element in Vuetify 3)
        const scrollers = [
          document.querySelector('.v-main__scroller'),
          document.querySelector('.v-main'),
          document.documentElement,
          document.body
        ]

        for (const scroller of scrollers) {
          if (scroller) {
            if (savedPosition) {
              scroller.scrollTop = savedPosition.top || 0
            } else {
              scroller.scrollTop = 0
            }
            break
          }
        }

        resolve(savedPosition || { top: 0 })
      }, 50)
    })
  },
})


// Domain redirect configuration
const domainRedirectConfig = {
  // Enable redirect for different environments
  development: {
    enabled: true,
    // Azure app registration only allows http when using localhost (without `www` subdomain)
    from: 'www.localhost', 
    to: 'localhost'
  },
  // Future configuration for other environments
  staging: {
    enabled: false,
    from: 'staging.yourdomain.com',
    to: 'www.staging.yourdomain.com'
  },
  production: {
    enabled: false,
    from: 'yourdomain.com',
    to: 'www.yourdomain.com'
  }
}

// Helper function to get current environment
function getCurrentEnvironment() {
  return process.env.NODE_ENV;
}

// Helper function to check and perform domain redirect
function checkDomainRedirect() {
  const currentEnv = getCurrentEnvironment()
  const config = domainRedirectConfig[currentEnv]
  
  if (!config || !config.enabled) {
    return false
  }
  
  const currentHost = window.location.host
  const currentProtocol = window.location.protocol
  const currentPath = window.location.pathname + window.location.search + window.location.hash
  
  // Check if we need to redirect (domain matches 'from' but doesn't already have 'to' prefix)
  if (currentHost.startsWith(config.from) && !currentHost.startsWith(config.to)) {
    const newHost = currentHost.replace(config.from, config.to)
    const newUrl = `${currentProtocol}//${newHost}${currentPath}`
    
    console.log(`Domain redirect: ${window.location.href} → ${newUrl}`)
    window.location.replace(newUrl)
    return true
  }
  
  return false
}

// Navigation guard for domain redirect and authentication
router.beforeEach(async (to, from, next) => {
  // console.log(`🔄 ROUTER GUARD START: ${from.path} → ${to.path}`)

  // Maintenance gate: sit above auth-init wait so /login and anonymous users
  // are covered too. App.vue watches the store and navigates here on activation.
  const maintenanceStore = useMaintenanceStore()
  if (maintenanceStore.active && to.name !== 'Maintenance') {
    return next({ name: 'Maintenance' })
  }

  // Skip domain redirect if MSAL is processing a redirect response (hash contains auth tokens)
  const hasMsalHash = window.location.hash && (
    window.location.hash.includes('id_token=') ||
    window.location.hash.includes('access_token=') ||
    window.location.hash.includes('code=') ||
    window.location.hash.includes('error=')
  )

  // Check for domain redirect first (but skip if MSAL is handling auth redirect)
  if (!hasMsalHash && checkDomainRedirect()) {
    console.log('🌐 Domain redirect triggered')
    // Redirect is happening, don't proceed with navigation
    return
  }
  
  // const authStore = useAuthStore()

  try {
    const authStore = useAuthStore()
    
    // Wait for auth initialization to complete before checking auth state.
    // Since the app is already mounted, the user sees the page layout
    // (not a blank page) while this await resolves.
    if (authStore.loading) {
      console.log('⏳ Auth still loading, waiting for initialization...')
      const result = await Promise.race([
        authStore.initPromise.then(() => 'done'),
        new Promise(resolve => setTimeout(() => resolve('timeout'), 10000)),
      ])
      if (result === 'timeout') {
        console.warn('⚠️ Auth initialization timed out after 10s, proceeding with current auth state')
      } else {
        console.log('✅ Auth initialization completed')
      }
    }

    const isAuthenticated = authStore.isAuthenticated
    const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
    const requiresManager = to.matched.some(record => record.meta.requiresManager)
    const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin)

    if (isAuthenticated && requiresAuth) {
      const loggedOut = await authStore.checkPermissionsOnNavigate()
      if (loggedOut) return
    }

    console.log('Router guard:', {
      to: to.path,
      toName: to.name,
      from: from.path,
      fromName: from.name,
      requiresAuth,
      requiresManager,
      requiresAdmin,
      isAuthenticated,
      loading: authStore.loading,
      user: authStore.user?.username || 'none',
      hasManagerRole: authStore.hasRole('Managers'),
      hasAdminRole: authStore.hasRole('Administrators'),
      roles: authStore.roles
    })

    if (requiresAuth && !isAuthenticated) {
      // Redirect to login if route requires auth and user is not authenticated
      console.log('Redirecting to login - authentication required')
      next('/login')
    } else if (to.name === 'Login' && isAuthenticated) {
      // Redirect to home if user is already authenticated and tries to access login
      console.log('Redirecting to home - already authenticated')
      next('/')
    } else if (requiresAdmin && !authStore.hasRole('Administrators')) {
      console.warn('Access denied - Administrators only route')
      next('/')
    } else if (requiresManager && !authStore.hasRole('Managers') && !authStore.hasRole('Administrators')) {
      console.warn('Access denied - Managers only route')
      next('/')
    } else if (requiresManager && to.params.id && !authStore.hasRole('Administrators')) {
      // Check if manager (not admin) is trying to access an organization-specific resource
      // Ensure they can only access their own organization or child teams
      // Administrators can access any organization

      const requiresOrgAccess = to.matched.some(record => record.meta.requiresOrgAccess)

      if (requiresOrgAccess) {
        const userOrgId = authStore.userProfile?.organisation?.id
        const requestedOrgId = to.params.id

        // Guard: user profile must have an organisation to access org-specific routes
        if (!userOrgId) {
          console.warn('Access denied - User has no organisation assigned')
          next({ name: 'NotFound' })
          return
        }

        const normalizedUserOrgId = userOrgId?.toString().toLowerCase()
        const normalizedRequestedOrgId = requestedOrgId?.toString().toLowerCase()

        if (normalizedUserOrgId && normalizedRequestedOrgId && normalizedUserOrgId !== normalizedRequestedOrgId) {
          // Direct match failed — check if the requested org is a descendant of the user's org
          try {
            let ancestorId = getParentOrg(normalizedRequestedOrgId)
            if (ancestorId === undefined) {
              const requestedOrg = await apiService.get(`/Organisations/${requestedOrgId}`)
              ancestorId = requestedOrg?.parentOrganisation?.id?.toString().toLowerCase() || null
              setParentOrg(normalizedRequestedOrgId, ancestorId)
            }

            // Walk up the parent chain (with caching) to handle deep hierarchies
            let currentParentId = ancestorId
            let depth = 0
            const MAX_HIERARCHY_DEPTH = 10
            while (currentParentId && currentParentId !== normalizedUserOrgId && depth < MAX_HIERARCHY_DEPTH) {
              let cachedParentId = getParentOrg(currentParentId)
              if (cachedParentId === undefined) {
                try {
                  const parentOrg = await apiService.get(`/Organisations/${currentParentId}`)
                  cachedParentId = parentOrg?.parentOrganisation?.id?.toString().toLowerCase() || null
                  setParentOrg(currentParentId, cachedParentId)
                } catch {
                  cachedParentId = null
                }
              }
              currentParentId = cachedParentId
              depth++
            }

            if (currentParentId === normalizedUserOrgId) {
              next()
            } else {
              console.warn('Access denied - Manager attempting to access different organization')
              console.warn(`User org: ${userOrgId}, Requested org: ${requestedOrgId}`)
              // Fail closed — redirect to user's own organisation dashboard
              next({ name: 'DashboardOrganisationOverview', params: { id: userOrgId } })
            }
          } catch (e) {
            // Fail closed for all errors (404, 500, network, timeout)
            console.warn('Access denied - Failed to verify organization access:', e?.statusCode, e?.message)
            next({ name: 'DashboardOrganisationOverview', params: { id: userOrgId } })
          }
          return
        }
      }

      next()
    } else {
      next()
    }
  } catch (error) {
    console.error('Router guard error:', error)
    // On error, allow navigation but log the issue
    next()
  }
  // console.log(`🏁 ROUTER GUARD END: ${from.path} → ${to.path}`)
})

// Set document title after each navigation
router.afterEach((to) => {
  const baseTitle = 'EAMS - Emergency Accommodation Management System'
  const pageTitle = to.meta?.title

  if (pageTitle) {
    document.title = `${pageTitle} | ${baseTitle}`
  } else {
    document.title = baseTitle
  }

  // A completed navigation proves chunk loading works again, so re-arm the
  // reload-once recovery below for any future chunk failure.
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY)
  } catch {
    // Storage unavailable (private mode/quota): recovery stays one-shot
  }
})

// Recover from lazy-route chunk load failures. These happen transiently (e.g.
// dev server under CI load) and after deploys, when a stale tab requests old
// hashed chunk files that no longer exist. A full page load of the target
// route fetches fresh index.html and chunk URLs. sessionStorage (survives the
// reload, per-tab) guards against a reload loop when a build is genuinely
// broken: one retry per navigation, re-armed only by a successful navigation.
const CHUNK_RELOAD_KEY = 'eams-chunk-error-reload'

router.onError((error, to) => {
  const isChunkLoadError =
    error?.name === 'ChunkLoadError' ||
    /loading (css )?chunk [^ ]+ failed/i.test(error?.message || '')
  if (!isChunkLoadError || !to?.fullPath) return

  try {
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === to.fullPath) return
    sessionStorage.setItem(CHUNK_RELOAD_KEY, to.fullPath)
  } catch {
    // Can't track reload attempts without storage; bail rather than risk a loop
    return
  }

  window.location.assign(to.fullPath)
})

export default router
